/**
 * Query operations for routes
 */

import { collection, query, where, orderBy, getDocs, limit as firestoreLimit } from 'firebase/firestore'
import { db } from '../../../lib/firebase'
import { RouteType, RouteStatus, UnifiedRoute } from './types'
import { deserializeFromFirebase } from '../utils'

/**
 * List routes with optional filters
 */
export const listRoutes = async (filters: {
  type?: RouteType | RouteType[]
  status?: RouteStatus | RouteStatus[]
  ownerId?: string
  clubId?: string
  limit?: number
}): Promise<UnifiedRoute[]> => {
  console.log('🔍 listRoutes called with filters:', filters)
  try {
    let q = query(collection(db, 'routes'))
    
    // Apply filters
    const constraints: any[] = []
    
    if (filters.ownerId) {
      constraints.push(where('ownerId', '==', filters.ownerId))
    }
    
    if (filters.type) {
      const types = Array.isArray(filters.type) ? filters.type : [filters.type]
      // Use == for single value to match security rules, in for multiple
      if (types.length === 1) {
        constraints.push(where('type', '==', types[0]))
      } else {
        constraints.push(where('type', 'in', types))
      }
    }

    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status]
      // Use == for single value to match security rules, in for multiple
      if (statuses.length === 1) {
        constraints.push(where('status', '==', statuses[0]))
      } else {
        constraints.push(where('status', 'in', statuses))
      }
    }
    
    if (filters.clubId) {
      constraints.push(where('submission.clubId', '==', filters.clubId))
      // Only show approved submissions
      constraints.push(where('submission.status', '==', 'approved'))
    }
    
    // Add ordering only if no filters at all (to avoid composite index requirements)
    // DO NOT add orderBy with other filters - it requires composite indexes
    if (constraints.length === 0) {
      constraints.push(orderBy('updatedAt', 'desc'))
    }
    
    // Add limit if specified
    if (filters.limit) {
      constraints.push(firestoreLimit(filters.limit))
    }
    
    q = query(collection(db, 'routes'), ...constraints)
    
    let snapshot
    try {
      snapshot = await getDocs(q)
      console.log(`📊 Query returned ${snapshot.docs.length} routes`)
    } catch (error: any) {
      // Check if it's a missing index error
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.error('🔥 Missing composite index for club routes query. Please create an index for:', {
          collection: 'routes',
          fields: ['submission.clubId', 'submission.status'],
          message: error.message
        })
        // For now, fall back to a simpler query
        console.log('📊 Falling back to simpler query without submission.status filter')
        const simpleQuery = query(
          collection(db, 'routes'), 
          where('submission.clubId', '==', filters.clubId),
          orderBy('updatedAt', 'desc')
        )
        snapshot = await getDocs(simpleQuery)
        // Filter approved submissions client-side
        const allDocs = snapshot.docs
        snapshot = {
          ...snapshot,
          docs: allDocs.filter(doc => {
            const data = doc.data()
            return data.submission?.status === 'approved'
          })
        }
        console.log(`📊 After client-side filtering: ${snapshot.docs.length} approved routes`)
      } else {
        throw error
      }
    }
    
    // Convert route documents directly - no need to load segments anymore!
    const routesWithStats = snapshot.docs.map(doc => {
      const rawData = doc.data()
      const data = deserializeFromFirebase(rawData)
      
      // Include minimal data for list views - just first coordinate for marker placement
      const deserializedData = {
        ...data,
        id: doc.id,
        coordinates: data.coordinates && data.coordinates.length > 0 ? [data.coordinates[0]] : [], // Only first coordinate for marker
        routes: [], // IMPORTANT: Keep routes empty so RouteLoadModal knows to load full data
        pois: [],
        photos: [],
        highlightImages: data.highlightImages, // Include highlight images for route list display
        createdAt: data.createdAt instanceof Date ? data.createdAt : new Date(),
        updatedAt: data.updatedAt instanceof Date ? data.updatedAt : new Date(),
        lastAutosaveAt: data.lastAutosaveAt instanceof Date ? data.lastAutosaveAt : undefined,
        submission: data.submission ? {
          ...data.submission,
          submittedAt: data.submission.submittedAt instanceof Date ? data.submission.submittedAt : new Date(),
          reviewedAt: data.submission.reviewedAt instanceof Date ? data.submission.reviewedAt : undefined
        } : undefined
      }
      return deserializedData as UnifiedRoute
    })
    
    return routesWithStats
  } catch (error) {
    console.error('Failed to list routes:', error)
    return []
  }
}